import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'content', label: 'Course Content' },
  { id: 'assignments', label: 'Assignments' },
  { id: 'meetings', label: 'Meetings' },
  { id: 'announcements', label: 'Announcements' },
  { id: 'progress', label: 'Progress' },
  { id: 'teacher', label: 'Teacher' },
];

const cardClass = 'bg-gray-900 border border-yellow-400/20 rounded-2xl p-6';

const resolveImageUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (!BACKEND_URL) return url;
  return `${BACKEND_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

const resolveProtectedUrl = (url) => {
  const resolved = resolveImageUrl(url);
  if (!resolved || !/\/api\/(files|employee)\//i.test(resolved)) return resolved;
  try {
    const token = localStorage.getItem('token');
    if (!token) return resolved;
    const separator = resolved.includes('?') ? '&' : '?';
    return `${resolved}${separator}access_token=${encodeURIComponent(token)}`;
  } catch (error) {
    return resolved;
  }
};

const EmptyState = ({ title, message }) => (
  <div className={cardClass}>
    <h3 className="text-yellow-400 font-bold mb-2">{title}</h3>
    <p className="text-gray-400 text-sm">{message}</p>
  </div>
);

const MyCourseDetailsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [denied, setDenied] = useState(false);
  const [payload, setPayload] = useState(null);

  const courseId = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    return segments[segments.length - 1] || '';
  }, [location.pathname]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setDenied(false);
        setError('');
        const response = await axios.get(`${API}/student/my-courses/${courseId}`);
        if (!active) return;
        setPayload(response.data || null);
      } catch (requestError) {
        if (!active) return;
        const status = requestError?.response?.status;
        if (status === 403) {
          setDenied(true);
          setError(requestError?.response?.data?.detail || 'Enrollment Required');
        } else {
          setError(requestError?.response?.data?.detail || 'Unable to load this course workspace.');
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [courseId]);

  if (loading) {
    return (
      <div className="bg-black min-h-screen text-white flex items-center justify-center">
        <div className="text-gray-400">Loading private course workspace...</div>
      </div>
    );
  }

  if (denied) {
    return (
      <div className="bg-black min-h-screen text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-gray-900 border border-yellow-400/30 rounded-2xl p-10 text-center">
            <h1 className="text-3xl font-bold text-yellow-400 mb-3">Enrollment Required</h1>
            <p className="text-gray-300 mb-6">{error || 'You need an active enrollment to access this private course workspace.'}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <button onClick={() => navigate('/student/my-courses')} className="bg-yellow-500 hover:bg-yellow-600 text-black px-5 py-2.5 rounded-lg font-semibold">Back to My Courses</button>
              <button onClick={() => navigate('/courses')} className="border border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black px-5 py-2.5 rounded-lg font-semibold">Open Public Catalog</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !payload?.course) {
    return (
      <div className="bg-black min-h-screen text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-red-900/40 border border-red-500 rounded-xl p-5 text-red-200">{error || 'Course not found.'}</div>
        </div>
      </div>
    );
  }

  const { course, content, assignments, meetings, announcements, progress, teacher } = payload;

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        <button onClick={() => navigate('/student/my-courses')} className="text-yellow-400 hover:text-yellow-300 text-sm font-medium">&larr; Back to My Courses</button>

        <header className="bg-gradient-to-r from-gray-900 to-black border border-yellow-400/20 rounded-2xl overflow-hidden">
          <div className="grid md:grid-cols-[260px_1fr] gap-0">
            <div className="h-48 md:h-full bg-gray-800">
              {course.thumbnail_url ? (
                <img src={resolveImageUrl(course.thumbnail_url)} alt={course.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-6xl">🎓</div>
              )}
            </div>
            <div className="p-6 md:p-8">
              <h1 className="text-3xl font-bold text-yellow-400 mb-2">{course.title}</h1>
              <p className="text-gray-300 mb-4">{course.description || 'Course workspace and learning materials.'}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                <div className="bg-gray-900 rounded-lg p-3 border border-yellow-400/15"><div className="text-gray-400">Instructor</div><div className="text-white font-semibold">{course.instructor?.name || 'K.R.A Instructor Team'}</div></div>
                <div className="bg-gray-900 rounded-lg p-3 border border-yellow-400/15"><div className="text-gray-400">Duration</div><div className="text-white font-semibold">{course.duration}</div></div>
                <div className="bg-gray-900 rounded-lg p-3 border border-yellow-400/15"><div className="text-gray-400">Enrollment</div><div className="text-white font-semibold capitalize">{String(course.enrollment_status || 'active').replaceAll('_', ' ')}</div></div>
                <div className="bg-gray-900 rounded-lg p-3 border border-yellow-400/15"><div className="text-gray-400">Payment</div><div className="text-white font-semibold capitalize">{String(course.payment_status || 'unknown').replaceAll('_', ' ')}</div></div>
              </div>
            </div>
          </div>
        </header>

        <div className="flex flex-wrap gap-2 border-b border-gray-800 pb-4">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-yellow-500 text-black' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <div className="space-y-4">
            <div className={cardClass}>
              <h3 className="text-yellow-400 font-bold mb-2">Course Description</h3>
              <p className="text-gray-300 text-sm">{course.description || 'No description provided yet.'}</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className={cardClass}>
                <h3 className="text-yellow-400 font-bold mb-2">Learning Objectives</h3>
                <ul className="text-gray-300 text-sm space-y-1">
                  {(course.learning_objectives || []).map((item, idx) => <li key={idx}>• {item}</li>)}
                  {(!(course.learning_objectives || []).length) && <li>No objectives published yet.</li>}
                </ul>
              </div>
              <div className={cardClass}>
                <h3 className="text-yellow-400 font-bold mb-2">Instructor Information</h3>
                <p className="text-white font-semibold">{course.instructor?.name || 'K.R.A Instructor Team'}</p>
                <p className="text-gray-400 text-sm mt-1">{course.instructor?.bio || 'Instructor profile not available.'}</p>
                <p className="text-gray-500 text-xs mt-2">Enrollment date: {course.enrollment_date ? new Date(course.enrollment_date).toLocaleDateString() : 'Recently'}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'content' && (
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className={cardClass}>
                <h3 className="text-yellow-400 font-bold mb-3">Modules & Lessons</h3>
                {(content.modules || []).length === 0 ? <p className="text-gray-400 text-sm">No modules yet.</p> : (
                  <div className="space-y-3">
                    {(content.modules || []).map((module) => (
                      <div key={module.id || module.title} className="bg-gray-800 rounded-lg p-3">
                        <div className="text-white font-semibold">{module.title || 'Module'}</div>
                        <div className="text-gray-400 text-xs mt-1">{(module.lessons || []).length} lesson(s)</div>
                        <ul className="text-gray-300 text-sm mt-2 space-y-1">
                          {(module.lessons || []).map((lesson, idx) => <li key={lesson.id || `${module.title}-${idx}`}>• {lesson.title || lesson}</li>)}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className={cardClass}>
                <h3 className="text-yellow-400 font-bold mb-3">Videos, PDFs & Downloads</h3>
                <div className="space-y-2 text-sm">
                  {(content.lectures || []).map((lecture) => (
                    <div key={lecture.id} className="bg-gray-800 rounded-lg p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-white font-medium">{lecture.title}</span>
                        <span className="text-xs capitalize bg-gray-700 text-gray-300 px-2 py-0.5 rounded-full">{lecture.resource_type}</span>
                      </div>
                      <a href={resolveProtectedUrl(lecture.resource_url)} target="_blank" rel="noreferrer" className="text-yellow-400 hover:underline text-xs mt-2 inline-block">Open Resource</a>
                    </div>
                  ))}
                  {(!(content.lectures || []).length) && <p className="text-gray-400">No published content yet.</p>}
                </div>
              </div>
            </div>

            <div className={cardClass}>
              <h3 className="text-yellow-400 font-bold mb-3">Resources</h3>
              {(content.resources || []).length === 0 ? <p className="text-gray-400 text-sm">No additional resources yet.</p> : (
                <div className="grid md:grid-cols-2 gap-3">
                  {(content.resources || []).map((item) => (
                    <div key={item.id} className="bg-gray-800 rounded-lg p-3">
                      <div className="text-white font-medium">{item.title}</div>
                      <a href={resolveProtectedUrl(item.resource_url)} target="_blank" rel="noreferrer" className="text-yellow-400 text-xs hover:underline">Open</a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'assignments' && (
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className={cardClass}>
                <h3 className="text-yellow-400 font-bold mb-3">Pending Assignments</h3>
                {(assignments.pending || []).length === 0 && <p className="text-gray-400 text-sm">No pending assignments.</p>}
                {(assignments.pending || []).map((item) => (
                  <div key={item.id} className="bg-gray-800 rounded-lg p-3 mb-2">
                    <div className="text-white font-medium">{item.title}</div>
                    <div className="text-gray-400 text-xs">Due: {item.due_date || 'No due date'}</div>
                  </div>
                ))}
              </div>

              <div className={cardClass}>
                <h3 className="text-yellow-400 font-bold mb-3">Submitted Assignments</h3>
                {(assignments.submitted || []).length === 0 && <p className="text-gray-400 text-sm">No submitted assignments yet.</p>}
                {(assignments.submitted || []).map((item) => (
                  <div key={item.id} className="bg-gray-800 rounded-lg p-3 mb-2">
                    <div className="text-white font-medium">{item.title}</div>
                    <div className="text-gray-400 text-xs">Grade: {item.grade !== null && item.grade !== undefined ? `${item.grade}/${item.max_score || 100}` : 'Not graded yet'}</div>
                    {item.feedback && <div className="text-gray-300 text-xs mt-1">Feedback: {item.feedback}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'meetings' && (
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className={cardClass}>
                <h3 className="text-yellow-400 font-bold mb-3">Upcoming Classes</h3>
                {(meetings.classes || []).length === 0 && <p className="text-gray-400 text-sm">No upcoming classes.</p>}
                {(meetings.classes || []).map((item) => (
                  <div key={item.id} className="bg-gray-800 rounded-lg p-3 mb-2">
                    <div className="text-white font-medium">{item.name}</div>
                    <div className="text-gray-400 text-xs">{item.date} • {item.start_time} - {item.end_time}</div>
                    {item.meeting_link && <a href={item.meeting_link} target="_blank" rel="noreferrer" className="text-yellow-400 text-xs hover:underline">Join Class Link</a>}
                  </div>
                ))}
              </div>

              <div className={cardClass}>
                <h3 className="text-yellow-400 font-bold mb-3">Meeting Links & Schedule</h3>
                {(meetings.upcoming || []).length === 0 && <p className="text-gray-400 text-sm">No meetings scheduled.</p>}
                {(meetings.upcoming || []).map((item) => (
                  <div key={item.id} className="bg-gray-800 rounded-lg p-3 mb-2">
                    <div className="text-white font-medium">{item.title}</div>
                    <div className="text-gray-400 text-xs">{item.date} • {item.start_time} - {item.end_time}</div>
                    {item.meeting_url && <a href={item.meeting_url} target="_blank" rel="noreferrer" className="text-yellow-400 text-xs hover:underline">Join Meeting</a>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'announcements' && (
          <div className="space-y-4">
            {(announcements || []).length === 0 && <EmptyState title="No announcements yet" message="Teacher updates for this course will appear here." />}
            {(announcements || []).map((item) => (
              <div key={item.id} className={cardClass}>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-yellow-400 font-bold">{item.title}</h3>
                  {item.is_pinned && <span className="text-xs bg-yellow-500 text-black px-2 py-0.5 rounded-full font-bold">Pinned</span>}
                </div>
                <p className="text-gray-300 text-sm">{item.content}</p>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'progress' && (
          <div className="space-y-4">
            <div className={cardClass}>
              <h3 className="text-yellow-400 font-bold mb-3">Overall Progress</h3>
              <div className="w-full h-3 bg-gray-800 rounded-full overflow-hidden mb-2">
                <div className="h-3 bg-gradient-to-r from-yellow-500 to-yellow-600" style={{ width: `${Number(progress.overall_percent || 0)}%` }}></div>
              </div>
              <p className="text-gray-300 text-sm">{Number(progress.overall_percent || 0)}% complete</p>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className={cardClass}><div className="text-gray-400 text-sm">Lessons Completed</div><div className="text-yellow-400 text-2xl font-bold">{progress.lessons_completed || 0}</div></div>
              <div className={cardClass}><div className="text-gray-400 text-sm">Assignments Completed</div><div className="text-yellow-400 text-2xl font-bold">{progress.assignments_completed || 0}</div></div>
              <div className={cardClass}><div className="text-gray-400 text-sm">Attendance</div><div className="text-yellow-400 text-2xl font-bold">{Number(progress.attendance_percent || 0)}%</div></div>
            </div>
          </div>
        )}

        {activeTab === 'teacher' && (
          <div className="space-y-4">
            <div className={cardClass}>
              <div className="flex items-start gap-4">
                <img src={resolveImageUrl(teacher.profile?.profile_photo_url) || 'https://api.dicebear.com/7.x/initials/svg?seed=Teacher'} alt={teacher.profile?.name || 'Teacher'} className="w-20 h-20 rounded-full object-cover border border-yellow-400/30" />
                <div>
                  <h3 className="text-xl font-bold text-yellow-400">{teacher.profile?.name || 'K.R.A Instructor Team'}</h3>
                  <p className="text-gray-300 text-sm mt-1">{teacher.profile?.bio || 'Instructor profile available.'}</p>
                  {teacher.contact_enabled && teacher.profile?.email ? (
                    <a href={`mailto:${teacher.profile.email}`} className="inline-block mt-3 bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg font-semibold text-sm">Contact Teacher</a>
                  ) : (
                    <p className="text-gray-500 text-xs mt-2">Direct contact is currently disabled.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCourseDetailsPage;
